import { action, makeObservable, observable, runInAction } from 'mobx';
import { ICompositeProductApi } from '@canvas-logic/clp-sdk';

import {
  attributeValueCase,
  createAttributeValue,
  getAttributeValue,
  hasAttributeValue,
  ICfgAttributeValue,
  RAL,
  ralCodeToHex,
  ralToHex,
} from '@canvas-logic/clp-domain';

import { IUICfgAttributeRal } from '@canvas-logic/clp-api-types';
import { AttributeStateBase } from '@/elements/base/AttributeStateBase';

let id = 0;

export interface Color {
  ral: string;
  hex: string;
}

export class RalAttributeState extends AttributeStateBase<IUICfgAttributeRal> {
  readonly elementKind = 'attribute';
  readonly elementType = 'RAL';
  public id = 'ral-view-state-' + ++id;

  color: {
    custom: boolean;
    value: Color;
  } | null = null;
  availableColors: Color[] = [];
  customColors: Color[] = [];
  loading = false;
  hasCustom = false;

  constructor(public readonly api: ICompositeProductApi, element: IUICfgAttributeRal) {
    super(api, element);
    // TODO: validate element
    // this.visible = this.api.isAvailable(this.componentKey);
    this.updateCurrent();
    if (!this.color) {
      throw new Error('Empty color');
    }
    this.availableColors = [this.color.value];

    makeObservable(this, {
      loading: observable,
      customColors: observable,
      availableColors: observable,
      color: observable,
      hasCustom: observable,
      update: action.bound,
      loadCustomColors: action.bound,
      setColor: action.bound,
      setCustomColor: action.bound,
    });

    this.update().then(() => this.loadCustomColors());
  }

  async update() {
    this.updateVisibility();
    if (this.visible) {
      this.loading = true;
      const values = (await this.api.loadCompatibleAttributeValues(this.componentKey, this.attributeKey)) ?? [];
      runInAction(() => {
        this.loading = false;
        // Same per-product re-read as AttributeStateBase.update: the component's
        // product may have changed, and labels/images are per-product (ADR-0051).
        this.textData = this.readTextData();
        this.availableColors = [];
        values.forEach((value) => {
          const valueCase = attributeValueCase(value);
          switch (valueCase.type) {
            case 'value': {
              const ral = valueCase.value.toString();
              this.availableColors.push({
                ral,
                hex: ralToHex(ral),
              });
              break;
            }
            case 'emptyCustom':
              this.hasCustom = true;
              break;
            case 'customValue':
              throw new Error('Invalid attribute value');
          }
        });
        this.updateCurrent();
      });
    }
  }

  async setColor(colorRal: string) {
    const attributeValue = createAttributeValue(colorRal);
    await this.api.setAttributeValue(this.componentKey, this.attributeKey, attributeValue);
    await this.update();
    this.handleUpdate();
  }

  async setCustomColor(colorRal: string) {
    const attributeValue: ICfgAttributeValue = { custom: true, value: colorRal };
    await this.api.setAttributeValue(this.componentKey, this.attributeKey, attributeValue);
    await this.update();
    this.handleUpdate();
  }

  protected handleUpdate() {
    this.notify({
      event: 'updateDependentAttributes',
      attributes: [],
      target: this,
    });
  }

  protected updateCurrent() {
    const colorAttributeValue = this.api.getAttributeValue(this.componentKey, this.attributeKey);
    if (colorAttributeValue === undefined) return;
    const code = getAttributeValue(colorAttributeValue).toString();
    const color: Color = {
      ral: code,
      hex: ralToHex(code),
    };
    this.color = {
      custom: colorAttributeValue.custom ?? false,
      value: color,
    };
  }

  async loadCustomColors() {
    this.customColors = [];
    const values = await this.api.loadCompatibleAttributeValues(this.componentKey, this.attributeKey);
    const usedRALs = new Set<string>();

    runInAction(() => {
      for (const attributeValue of values) {
        if (hasAttributeValue(attributeValue)) {
          usedRALs.add(attributeValue.value as string);
        }
      }
      for (const code of RAL.codes) {
        const ral = `RAL ${code}`;
        if (!usedRALs.has(ral)) {
          this.customColors.push({
            ral: ral,
            hex: ralCodeToHex(code),
          });
        }
      }
    });
  }
}
